import {
  type ApiProblemDetails,
  postPayPointsPublicIDScan,
  type StartPayPointTransactionResponse,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { skipToken, useQuery } from "@tanstack/react-query";

const SCAN_PAY_POINT_QUERY_KEY = "SCAN_PAY_POINT";

export const scanPayPointKeys = {
  all: [SCAN_PAY_POINT_QUERY_KEY],
  byId: (publicID?: string | null) => [...scanPayPointKeys.all, publicID],
};

// The scan endpoint is a POST that starts a transaction server-side, so it
// technically doesn't fit `useQuery`'s "idempotent read" contract. We use a
// query anyway because the Fixed Collector page treats it as initialization:
// given the paypoint ID from the URL, fetch the transaction to pay for. A
// mutation-based version had to reimplement query semantics by hand with a
// useEffect + useRef one-shot guard.
//
// Every automatic refetch is disabled so we still scan exactly once per page
// visit — the only re-fires are an explicit refetch() (Retry) or a paypoint ID
// change (new query key).
export const useScanPayPointQuery = (publicID?: string | null) =>
  useQuery<StartPayPointTransactionResponse, ApiProblemDetails>({
    queryKey: scanPayPointKeys.byId(publicID),
    queryFn: publicID
      ? processResponseWithApiProblemDetails(() =>
          postPayPointsPublicIDScan({ tipAmount: 0 }, publicID),
        )
      : skipToken,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
